import { AlertTriangle, Check } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from '#router'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ARCHIVE_GROUP_LABELS,
  ARCHIVE_GROUP_STAGE,
  ARCHIVE_GROUPS,
  type ArchiveGroup,
  type CallFacet,
  formatNeedsAction,
  LANE_LABELS,
  LIVE_PIPELINE_STAGES,
  type QueueGroup,
  type QueueSort,
  STAGE_LABELS,
} from '@/lib/audition/queue'
import type { AuditionStage } from '@/lib/audition/state-machine'
import { trpc } from '@/lib/trpc'
import { useReviewLens } from './hooks/use-auditions'
import { CALL_LABELS } from './labels'
import {
  archiveHref,
  ControlBand,
  FilterChip,
  LaneBand,
  type LaneFilter,
  LensBand,
  QueueRowItem,
  SearchBox,
  SortBand,
} from './queue-parts'

/** A queue group this page renders: everything that is not an archive. */
type LiveGroup = Exclude<QueueGroup, ArchiveGroup>

/**
 * The order an admin reads them in: what they owe first, then what they are
 * waiting on. The archives are not sections of this page any more (LG-914) —
 * they are the half of the table that grows with marketing traffic, read a
 * page at a time on their own page, and linked from the strip at the bottom.
 */
const LIVE_GROUPS = [
  'needs_action',
  'waiting_on_applicant',
] as const satisfies readonly LiveGroup[]

/**
 * Coverage, which `satisfies readonly LiveGroup[]` cannot express: it checks
 * that every entry IS a live group, never that every live group is an entry.
 * This is the list the render iterates, so a group missing from it renders
 * under no heading at all.
 */
const _everyLiveGroupIsOrdered: Exclude<
  LiveGroup,
  (typeof LIVE_GROUPS)[number]
> extends never
  ? true
  : ['LiveGroup missing from LIVE_GROUPS'] = true

const GROUP_TITLES = {
  needs_action: 'Needs action',
  waiting_on_applicant: 'Waiting on the applicant',
} as const satisfies Record<LiveGroup, string>

/**
 * One pill per call fact (LG-1052), labelled by the same table the row chips
 * read from. Seven is a lot for one band, and the pair that could fold back
 * into one is the two "w/o a call" pills — but this page never lists an
 * accepted or rejected applicant (those are archive stages), so on it the
 * two select different people: `payment_unlocked` rows let in early, and
 * live rows whose offer was withdrawn. Merging them would re-create the
 * conflation the split exists to end.
 */
const CALL_FILTERS = [
  {
    key: 'none',
    label: 'No call yet',
    title: 'Nothing offered and no decision made',
  },
  {
    key: 'offered',
    label: CALL_LABELS.offered,
    title: 'Slots proposed, or a booking pending the eligibility gate',
  },
  {
    key: 'scheduled',
    label: CALL_LABELS.scheduled,
    title: 'A slot is locked in and the call is still ahead',
  },
  { key: 'held', label: CALL_LABELS.held, title: 'An intro call was held' },
  {
    key: 'no_show',
    label: CALL_LABELS.no_show,
    title: 'Booked a call and did not attend',
  },
  {
    key: 'accepted_no_call',
    label: CALL_LABELS.accepted_no_call,
    title: 'Let in without ever talking',
  },
  {
    key: 'rejected_no_call',
    label: CALL_LABELS.rejected_no_call,
    title: 'Refused without a call, or the offer was withdrawn',
  },
] as const satisfies readonly { key: CallFacet; label: string; title: string }[]

/**
 * Same coverage check as `LIVE_GROUPS`: the annotation says every pill IS a
 * facet, never that every facet HAS a pill. `CALL_LABELS` and the chip
 * variants are `Record`s and go red on a widened union by themselves; this
 * ordered list would not, and rows would wear a chip nobody can filter on.
 */
const _everyCallFacetHasAPill: Exclude<
  CallFacet,
  (typeof CALL_FILTERS)[number]['key']
> extends never
  ? true
  : ['CallFacet missing from CALL_FILTERS'] = true

/**
 * The three archives as links with their SQL counts (LG-914).
 *
 * Always rendered, including over an empty pipeline: it is the only way from
 * this page to an applicant who is not in flight. Unknown is not zero — no
 * number until `laneMix` has answered, and none while it has failed.
 */
function ArchiveStrip({
  stageCounts,
  known,
}: {
  stageCounts: Partial<Record<AuditionStage, number>>
  known: boolean
}) {
  return (
    <nav
      aria-label="Archives"
      className="border-border flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t pt-4 text-sm"
    >
      <span className="text-muted-foreground font-medium">Archives</span>
      {ARCHIVE_GROUPS.map((group) => (
        <Link
          key={group}
          href={archiveHref(group)}
          className="text-primary underline-offset-4 hover:underline"
        >
          {ARCHIVE_GROUP_LABELS[group]}
          {known && (
            <span className="text-muted-foreground ml-1 text-xs">
              {stageCounts[ARCHIVE_GROUP_STAGE[group]] ?? 0}
            </span>
          )}
        </Link>
      ))}
    </nav>
  )
}

/**
 * Dims its children and marks them busy while a re-sort or re-filter is in
 * flight, so briefly-stale rows — and the banner and headline derived from
 * them — never read as the crisp answer. One definition for the page's two
 * stale bands; the control bands between them stay interactive and undimmed.
 * The archive page's variant is deliberately different (status-machine-driven,
 * busy on first load too) — do not unify them.
 */
function StaleWhileRefreshing({
  refreshing,
  children,
}: {
  refreshing: boolean
  children: React.ReactNode
}) {
  return (
    <div
      aria-busy={refreshing || undefined}
      className={`space-y-5 transition-opacity ${refreshing ? 'opacity-60' : ''}`}
    >
      {children}
    </div>
  )
}

/**
 * The applications review queue (LG-414): the live pipeline, bounded (LG-914).
 *
 * Ordered longest-waiting-first because the previous newest-first order put
 * the people who had waited longest where nobody looks, and nothing anywhere
 * said that anyone was waiting at all.
 *
 * Grouping, the owed sentence, the overdue flag and the facets are all
 * computed by the router — this component filters, searches and renders. The
 * split matters: the page's actual behavior stays testable at the tRPC seam.
 */
export function AuditionsHub() {
  const [sort, setSort] = useState<QueueSort>('longest_waiting')
  const [lens, setLens] = useReviewLens()
  const [search, setSearch] = useState('')
  const [needsOnly, setNeedsOnly] = useState(false)
  const [stageFilter, setStageFilter] = useState<AuditionStage | null>(null)
  const [laneFilter, setLaneFilter] = useState<LaneFilter | null>(null)
  const [paidOnly, setPaidOnly] = useState(false)
  const [callFilter, setCallFilter] = useState<CallFacet | null>(null)

  const list = trpc.admin.auditions.list.useQuery(
    // `stage` narrows the read, not just the page: it is the only control that
    // can reach applicants past LIVE_PIPELINE_CAP, which is what the
    // truncation banner sends the admin to.
    { sort, stage: stageFilter ?? undefined },
    {
      // `sort` is part of the query key, so toggling it asks for a key with
      // nothing cached — which reported `pending` and replaced the WHOLE
      // page with the skeleton, taking keyboard focus with it.
      placeholderData: (previous) => previous,
    },
  )
  // Keeping the old rows on screen is only right if they do not read as the
  // answer: the groups dim and mark themselves busy off this.
  const refreshing = list.isPlaceholderData
  const laneMix = trpc.admin.auditions.laneMix.useQuery()

  const rows = useMemo(() => list.data?.rows ?? [], [list.data])
  const laneMixText = (laneMix.data?.byLane ?? [])
    .map(
      (entry) =>
        `${entry.count} ${entry.lane ? LANE_LABELS[entry.lane].toLowerCase() : 'no path yet'}`,
    )
    .join(' · ')

  const query = search.trim()
  // MIRROR: the name/email match below and `admin.auditions.browse`'s `q`
  // predicate answer the SAME typed words in sequence — a fruitless search
  // here hands `?q=` to the archives. The wire prevents shared code, so the
  // shared semantics are pinned on both sides: case-insensitive substring
  // over email, first, last, and the composed "first last".
  const filtered = useMemo(() => {
    const needle = query.toLowerCase()
    return rows.filter((row) => {
      if (needsOnly && !row.needsAction) return false
      if (stageFilter && row.stage !== stageFilter) return false
      if (laneFilter && (row.lane ?? 'none') !== laneFilter) return false
      if (paidOnly && !row.hasPaid) return false
      if (callFilter && row.call !== callFilter) return false
      if (!needle) return true
      return (
        `${row.firstName} ${row.lastName}`.toLowerCase().includes(needle) ||
        row.email.toLowerCase().includes(needle)
      )
    })
  }, [rows, query, needsOnly, stageFilter, laneFilter, paidOnly, callFilter])

  // Exact over the whole funnel even though `rows` holds no archives:
  // `needsAction` and `overdue` are false for every archive stage by
  // construction, so counting the live pipeline counts everything that can
  // be true. Two things narrow `rows` and therefore these numbers, and both
  // are said out loud: the stage chip (narrows the SQL read) and the cap.
  const needsAction = rows.filter((row) => row.needsAction).length
  const overdue = rows.filter((row) => row.overdue).length
  const atLeast = list.data?.truncated ? 'at least ' : ''
  const countScope = stageFilter ? `${STAGE_LABELS[stageFilter]}: ` : ''

  // Counted in SQL by `laneMix`, not by measuring `rows`, which does not hold
  // the archives — the strip's numbers and the whole-funnel total read here.
  const stageCounts = useMemo(() => {
    const counts: Partial<Record<AuditionStage, number>> = {}
    for (const entry of laneMix.data?.byStage ?? [])
      counts[entry.stage] = entry.count
    return counts
  }, [laneMix.data])
  const totalApplicants = (laneMix.data?.byStage ?? []).reduce(
    (sum, entry) => sum + entry.count,
    0,
  )
  const totalLabel = laneMix.data ? ` · ${totalApplicants} total` : ''

  // Not while a refetch is in flight: switching stage chips filters the
  // HELD-OVER page by the new stage, which empties it every time — and a
  // dimmed "no matches" is still an answer the server has not given yet.
  const nothingMatches = filtered.length === 0 && !refreshing

  // Whole-page only when there is nothing to fall back to. A refetch that
  // fails while rows are on screen — the common one, since every decision
  // invalidates this query — keeps the page and says so in a banner.
  if (list.isError && rows.length === 0) {
    return (
      <div className="mx-auto max-w-5xl p-6 text-center">
        <h2 className="text-destructive text-lg font-semibold">
          Couldn't load applications
        </h2>
        <p className="text-muted-foreground mx-auto mt-2 max-w-md text-sm">
          Something went wrong on our side. Nobody's application was lost.
        </p>
        <Button className="mt-4" onClick={() => list.refetch()}>
          Retry
        </Button>
      </div>
    )
  }

  if (list.data === undefined) {
    return (
      <div className="mx-auto max-w-5xl space-y-3 p-6" aria-busy="true">
        <p className="text-muted-foreground text-sm">Loading applications…</p>
        {[0, 1, 2, 3, 4].map((index) => (
          <Skeleton key={index} className="h-14 w-full" />
        ))}
      </div>
    )
  }

  // An empty live pipeline is not an empty funnel — everybody could be a
  // client, or still filling in the form. Only `laneMix`, which counts every
  // stage in SQL, can tell those apart, so this waits for it.
  if (
    rows.length === 0 &&
    laneMix.data !== undefined &&
    totalApplicants === 0
  ) {
    return (
      <div className="mx-auto max-w-5xl p-6 text-center">
        <h2 className="text-lg font-semibold">No applications yet</h2>
        <p className="text-muted-foreground mx-auto mt-2 max-w-md text-sm">
          When somebody finishes the application form, they appear here.
        </p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-6">
      <h1 className="text-2xl font-semibold">Applications</h1>

      {/* `laneMix` is load-bearing — the stage chips, the total and the
          archive strip's counts read off it — so its failure has to say so
          rather than render as an empty pipeline. */}
      {laneMix.isError && (
        <p
          role="alert"
          className="border-destructive/50 bg-destructive/5 flex flex-wrap items-center gap-2 rounded-md border p-3 text-sm"
        >
          Couldn't load the pipeline counts — stage filters and archive totals
          are unavailable.
          <Button
            variant="link"
            className="h-auto p-0"
            onClick={() => laneMix.refetch()}
          >
            Retry
          </Button>
        </p>
      )}

      {list.isError && (
        <p
          role="alert"
          className="border-destructive/50 bg-destructive/5 flex flex-wrap items-center gap-2 rounded-md border p-3 text-sm"
        >
          Couldn't refresh — these rows may be out of date.
          <Button
            variant="link"
            className="h-auto p-0"
            onClick={() => list.refetch()}
          >
            Retry
          </Button>
        </p>
      )}

      {/* `countScope` already names the NEW stage filter, so the headline
          joins the rows' stale treatment rather than reading crisp. */}
      <StaleWhileRefreshing refreshing={refreshing}>
        {list.data?.truncated && (
          <p
            role="status"
            className="border-destructive/50 bg-destructive/5 rounded-md border p-3 text-sm"
          >
            {/* The cap applies to the stage-narrowed read as well, so telling an
                admin who has ALREADY picked a stage to pick one sends them round
                in a circle. */}
            {`More applicants are in flight than this page can hold — these are the first ${rows.length} in the current order. ${
              stageFilter
                ? 'This stage alone is over the limit, so the rest cannot be reached from here.'
                : 'Filter by pipeline stage to narrow the read and reach the rest.'
            }`}
          </p>
        )}

        {needsAction > 0 ? (
          <p className="text-lg">
            <strong className="font-semibold">
              {`${countScope}${atLeast}${formatNeedsAction(needsAction)}`}
            </strong>
            {overdue > 0 && (
              <span className="text-destructive font-medium">
                {' · '}
                <AlertTriangle
                  aria-hidden="true"
                  className="inline size-4 align-[-2px]"
                />
                {` ${atLeast}${overdue} overdue`}
              </span>
            )}
            <span className="text-muted-foreground">{totalLabel}</span>
          </p>
        ) : (
          <p className="text-muted-foreground flex items-center gap-2 text-lg">
            <Check aria-hidden="true" className="size-4" />
            {countScope
              ? `${countScope}nothing needs your action${totalLabel}`
              : `Nothing needs your action${totalLabel}`}
          </p>
        )}
      </StaleWhileRefreshing>

      <ControlBand
        label="Pipeline stage"
        hint={
          laneMixText ? (
            <span
              className="text-muted-foreground text-xs"
              title="Guardrail: how many applicants took each path"
            >
              {`Lane mix: ${laneMixText}`}
            </span>
          ) : undefined
        }
      >
        {/* Live stages only — and load-bearing: `list` refuses an archive
            stage outright (BAD_REQUEST), so a chip outside this set would
            throw, not empty the page. The way to those rows is the strip
            below. */}
        {LIVE_PIPELINE_STAGES.filter(
          (stage) => (stageCounts[stage] ?? 0) > 0,
        ).map((stage) => (
          <FilterChip
            key={stage}
            active={stageFilter === stage}
            onClick={() =>
              setStageFilter((prev) => (prev === stage ? null : stage))
            }
          >
            {`${STAGE_LABELS[stage]} ${stageCounts[stage]}`}
          </FilterChip>
        ))}
      </ControlBand>

      <LaneBand lane={laneFilter} onChange={setLaneFilter} />

      {/* Paid and Call ride together because they are the same kind of thing:
          facts about an applicant that their stage cannot express. */}
      <ControlBand label="Pipeline facts">
        <FilterChip
          active={paidOnly}
          onClick={() => setPaidOnly((prev) => !prev)}
        >
          Paid
        </FilterChip>
        {CALL_FILTERS.map((filter) => (
          <FilterChip
            key={filter.key}
            active={callFilter === filter.key}
            onClick={() =>
              setCallFilter((prev) => (prev === filter.key ? null : filter.key))
            }
            title={filter.title}
          >
            {filter.label}
          </FilterChip>
        ))}
      </ControlBand>

      {/* Sort keeps a band of its own, away from the filters. The chips look
          identical, but re-ordering the page and removing rows from it are
          opposite operations, and reading them as one set of six filters is
          what made the controls hard to trust. */}
      <div className="flex flex-wrap items-start gap-x-10 gap-y-5">
        <SortBand sort={sort} onChange={setSort} />

        <ControlBand label="Show">
          <FilterChip
            active={needsOnly}
            onClick={() => setNeedsOnly((prev) => !prev)}
          >
            Needs action only
          </FilterChip>
          <SearchBox value={search} onChange={setSearch} />
        </ControlBand>

        <LensBand lens={lens} onChange={setLens} className="ml-auto" />
      </div>

      <StaleWhileRefreshing refreshing={refreshing}>
        {nothingMatches ? (
          <div className="space-y-2 py-10 text-center text-sm">
            <p className="text-muted-foreground">
              No applications in the pipeline match the current filters.{' '}
              <Button
                variant="link"
                onClick={() => {
                  setSearch('')
                  setStageFilter(null)
                  setLaneFilter(null)
                  setNeedsOnly(false)
                  setPaidOnly(false)
                  setCallFilter(null)
                }}
              >
                Clear filters
              </Button>
            </p>
            {/* A search that stops at the pipeline answers "no matches" for
                someone sitting in an archive, which is worse than no search at
                all. The query travels with each link. */}
            {query && (
              <nav
                aria-label="Search the archives"
                className="text-muted-foreground flex flex-wrap justify-center gap-x-3"
              >
                <span>{`Search the archives for “${query}”:`}</span>
                {ARCHIVE_GROUPS.map((group) => (
                  <Link
                    key={group}
                    href={archiveHref(group, query)}
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    {ARCHIVE_GROUP_LABELS[group]}
                  </Link>
                ))}
              </nav>
            )}
          </div>
        ) : (
          LIVE_GROUPS.map((key) => {
            const groupRows = filtered.filter((row) => row.group === key)
            if (groupRows.length === 0) return null
            return (
              <section key={key} className="space-y-1">
                <div className="flex items-baseline gap-2">
                  <h2
                    className={`text-sm font-semibold uppercase tracking-wide ${
                      key === 'needs_action'
                        ? 'text-destructive'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {GROUP_TITLES[key]}
                  </h2>
                  <span className="text-muted-foreground text-xs">
                    {groupRows.length}
                  </span>
                </div>
                <ul className="divide-border divide-y rounded-md border">
                  {groupRows.map((row) => (
                    <QueueRowItem key={row.id} row={row} lens={lens} />
                  ))}
                </ul>
              </section>
            )
          })
        )}
      </StaleWhileRefreshing>

      <ArchiveStrip
        stageCounts={stageCounts}
        known={laneMix.data !== undefined}
      />
    </div>
  )
}
